// templateUtils.js - Helper functions for template parsing and formatting

import { FIELD_MAPPINGS, FIELD_TYPE_MAP, FIELD_SORT_ORDER } from './templateConstants';

/**
 * Format date to YYYY/MM/DD for date picker
 */
export const formatDateForPicker = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * Format date for display (e.g., "Jan 15, 2025")
 */
export const formatDateForDisplay = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get field type based on field name and template category
 */
export const getFieldType = (fieldName, templateCategory) => {
  const typeConfig = FIELD_TYPE_MAP[fieldName];
  
  if (!typeConfig) return 'Text Input';
  
  if (typeof typeConfig === 'string') return typeConfig;
  
  // Handle category-specific types
  return typeConfig[templateCategory] || typeConfig.default;
};

/**
 * Parse template string and extract fields
 */
export const parseTemplate = (template, templateCategory) => {
  const fieldRegex = /\[(.*?)\]/g;
  const fields = [];
  let match;
  
  while ((match = fieldRegex.exec(template)) !== null) {
    const fieldName = match[1];
    fields.push({
      placeholder: match[0],
      fieldName: fieldName,
      fieldType: getFieldType(fieldName, templateCategory)
    });
  }
  
  return fields;
};

/**
 * Sort fields by predefined order
 */
export const sortFields = (fields) => {
  return [...fields].sort((a, b) => {
    const aBaseType = a.fieldType.replace(' Multi', '');
    const bBaseType = b.fieldType.replace(' Multi', '');
    const aOrder = FIELD_SORT_ORDER[aBaseType] || 99;
    const bOrder = FIELD_SORT_ORDER[bBaseType] || 99;
    return aOrder - bOrder;
  });
};

/**
 * Get dropdown options from filter definitions
 */
export const getDropdownOptions = (filterDefinitions, fieldType) => {
  if (!filterDefinitions || !Array.isArray(filterDefinitions)) {
    return [];
  }

  const possibleNames = FIELD_MAPPINGS[fieldType] || [];
  
  const filterDef = filterDefinitions.find(def => {
    return possibleNames.some(name =>
      def.mbName === name ||
      def.displayName === fieldType ||
      def.displayName?.toLowerCase().includes(fieldType.toLowerCase())
    );
  });

  if (!filterDef?.values) {
    return [];
  }

  // Special handling for Category field
  if (filterDef.displayName === "Category") {
    return filterDef.values
      .sort((a, b) => a.split(">")[2]?.localeCompare(b.split(">")[2]))
      .map((x, i) => ({
        id: i,
        value: x,
        label: x.split(">")[2] ?? x,
      }));
  }

  return filterDef.values.sort().map((x, i) => ({
    id: i,
    value: x,
    label: x,
  }));
};

/**
 * Build query string from template and values
 */
export const buildQueryFromTemplate = (template, values) => {
  let query = template;
  
  Object.entries(values).forEach(([fieldName, value]) => {
    if (value) {
      const displayValue = Array.isArray(value)
        ? value.length > 1
          ? value.join('", "')
          : value[0]
        : value;
      query = query.replace(`[${fieldName}]`, displayValue);
    }
  });
  
  return query;
};

/**
 * Get date preset options based on max date
 */
export const getDatePresetOptions = (maxDate) => {
  // Use maxDate directly - it should always be provided from the component
  // Don't fall back to Date.now() as that would use today instead of the last available data date
  const end = maxDate instanceof Date ? maxDate : new Date(maxDate);
  
  return [
    {
      id: 'last-week',
      label: 'Last Week',
      getValue: () => {
        const start = new Date(end);
        start.setDate(start.getDate() - 7);
        return { startDate: formatDateForPicker(start), endDate: formatDateForPicker(end) };
      }
    },
    {
      id: 'last-4-weeks',
      label: 'Last 4 Weeks',
      getValue: () => {
        const start = new Date(end);
        start.setDate(start.getDate() - 28);
        return { startDate: formatDateForPicker(start), endDate: formatDateForPicker(end) };
      }
    },
    {
      id: 'last-13-weeks',
      label: 'Last 13 Weeks',
      getValue: () => {
        const start = new Date(end);
        start.setDate(start.getDate() - 91);
        return { startDate: formatDateForPicker(start), endDate: formatDateForPicker(end) };
      }
    },
    {
      id: 'year-to-date',
      label: 'Year to Date',
      getValue: () => {
        const start = new Date(end.getFullYear(), 0, 1);
        return { startDate: formatDateForPicker(start), endDate: formatDateForPicker(end) };
      }
    }
  ];
};

/**
 * Check if all required fields are complete
 */
export const isTemplateComplete = (fields, values) => {
  return fields.every(field => {
    const value = values[field.fieldName];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });
};
